import { Component, ElementRef, ViewChild, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@/layout/service/layout.service';
import { AppBreadcrumb } from './app.breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { FormsModule } from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { forkJoin } from 'rxjs';
import { CredentialsService } from '@/features/auth/data-access/credentials.service';
import { UserRepository } from '@/features/user/user.repository';
import { UserListItem } from '@/features/user/models/user';
import { ReserveRepository } from '@/features/reserve/reserve.repository';
import { Reserve } from '@/features/reserve/models/reserve';
import { AppService } from '@/shared/services/app.service';
import { ReserveViewDialogComponent } from '@/features/reserve/components/reserve-view-dialog.component';

interface QuickBookingItem {
    id: string;
    invoice: string;
    code: string;
    label: string;
    reserve: Reserve;
}

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, StyleClassModule, AppBreadcrumb, InputTextModule, ButtonModule, IconFieldModule, InputIconModule, AutoCompleteModule],
    template: `<div class="layout-topbar">
        <div class="topbar-start">
            <button #menubutton type="button" class="topbar-menubutton p-link p-trigger hover:cursor-pointer" (click)="onMenuButtonClick()">
                <i class="pi pi-bars"></i>
            </button>
            <nav app-breadcrumb class="topbar-breadcrumb"></nav>
        </div>

        <div class="topbar-end">
            <ul class="topbar-menu">
                <li class="min-w-[7.5rem]">
                    <p-button
                        label="Booking"
                        icon="pi pi-plus"
                        severity="success"
                        [outlined]="true"
                        size="small"
                        styleClass="w-full sm:w-auto"
                        (onClick)="onCreateBookingClick()"
                    ></p-button>
                </li>
                <li class="topbar-search flex shrink-0 items-center w-44 sm:w-56 md:w-64 lg:w-[20rem] xl:w-[22rem]">
                    <p-iconfield class="w-full">
                        <p-inputicon class="pi pi-user text-pink-500"></p-inputicon>
                        <p-autocomplete
                            [ngModel]="userSearchTerm"
                            (ngModelChange)="userSearchTerm = $event"
                            [suggestions]="userSuggestions()"
                            (completeMethod)="searchUsers($event)"
                            optionLabel="label"
                            placeholder="Search user"
                            [minLength]="2"
                            appendTo="body"
                            class="w-full"
                            (onSelect)="onUserSelected($event.value)"
                        ></p-autocomplete>
                    </p-iconfield>
                </li>
                <li class="topbar-search flex shrink-0 items-center w-44 sm:w-56 md:w-64 lg:w-[20rem] xl:w-[22rem]">
                    <p-iconfield class="w-full">
                        <p-inputicon class="pi pi-shopping-cart text-orange-500"></p-inputicon>
                        <p-autocomplete
                            [ngModel]="bookingSearchTerm"
                            (ngModelChange)="bookingSearchTerm = $event"
                            [suggestions]="bookingSuggestions()"
                            (completeMethod)="searchBookings($event)"
                            optionLabel="label"
                            placeholder="Search booking"
                            [minLength]="3"
                            appendTo="body"
                            class="w-full"
                            (onSelect)="onBookingSelected($event.value)"
                        ></p-autocomplete>
                    </p-iconfield>
                </li>
                <li class="ml-3">
                    <p-button icon="pi pi-palette" rounded (onClick)="onConfigButtonClick()"></p-button>
                </li>
                <li class="topbar-profile ml-3">
                    <button type="button" class="p-link hover:cursor-pointer" (click)="onProfileButtonClick()">
                        <img src="/layout/images/avatar.png" alt="Profile" />
                    </button>
                </li>
            </ul>
        </div>
    </div>`,
    styles: [`
        :host ::ng-deep .topbar-search .p-autocomplete {
            width: 100% !important;
        }

        :host ::ng-deep .topbar-search .p-autocomplete-input {
            width: 100% !important;
            min-width: 0;
        }
    `]
})
export class AppTopbar {
    @ViewChild('menubutton') menuButton!: ElementRef;

    readonly userSuggestions = signal<Array<UserListItem & { label: string }>>([]);
    readonly bookingSuggestions = signal<QuickBookingItem[]>([]);

    userSearchTerm: UserListItem | string | null = null;
    bookingSearchTerm: QuickBookingItem | string | null = null;

    private readonly appService = inject(AppService);
    private readonly userRepository = inject(UserRepository);
    private readonly reserveRepository = inject(ReserveRepository);

    constructor(public layoutService: LayoutService, public credentialsService: CredentialsService) {}

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    onProfileButtonClick() {
        this.layoutService.showProfileSidebar();
    }

    onConfigButtonClick() {
        this.layoutService.showConfigSidebar();
    }

    onCreateBookingClick() {
        void this.appService.router.navigateByUrl('/user/create-booking');
    }

    searchUsers(event: AutoCompleteCompleteEvent) {
        const query = (event.query ?? '').trim();
        if (!query) {
            this.userSuggestions.set([]);
            return;
        }
        this.userRepository.search(query, ['fname', 'lname', 'email', 'mobile']).subscribe({
            next: (users) => {
                const items = (users ?? []).map((user) => ({
                    ...user,
                    label: `${user.email} - ${user.fname} ${user.lname}`.trim()
                }));
                this.userSuggestions.set(items);
            },
            error: () => this.userSuggestions.set([])
        });
    }

    onUserSelected(user: UserListItem | null) {
        if (!user?.id) {
            return;
        }
        this.userSearchTerm = null;
        void this.appService.router.navigateByUrl(`/user/${user.id}`);
    }

    searchBookings(event: AutoCompleteCompleteEvent) {
        const query = (event.query ?? '').trim();
        if (!query) {
            this.bookingSuggestions.set([]);
            return;
        }
        forkJoin([
            this.reserveRepository.searchTransaction({ gdInvoiceId: query }),
            this.reserveRepository.searchTransaction({ code: query })
        ]).subscribe({
            next: ([byInvoice, byCode]) => {
                const map = new Map<string, QuickBookingItem>();
                for (const reserve of [...(byInvoice ?? []), ...(byCode ?? [])]) {
                    const id = reserve.id ?? '';
                    if (!id || map.has(id)) {
                        continue;
                    }
                    const invoice = reserve.gdInvoiceId?.trim() || '-';
                    const code = reserve.code?.trim() || '-';
                    map.set(id, {
                        id,
                        invoice,
                        code,
                        label: `${invoice} / ${code}`,
                        reserve
                    });
                }
                this.bookingSuggestions.set(Array.from(map.values()).slice(0, 20));
            },
            error: () => this.bookingSuggestions.set([])
        });
    }

    onBookingSelected(item: QuickBookingItem | null) {
        if (!item?.reserve) {
            return;
        }
        this.bookingSearchTerm = null;
        this.appService.openDialog(ReserveViewDialogComponent, `Reserve ${item.code}`, { reserve: item.reserve }, {
            width: '72rem',
            breakpoints: { '1200px': '95vw' },
            modal: true
        });
    }

}
